import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { NumInput as InputNumber } from "../components/NumInput";
import type { ColumnsType } from "antd/es/table";

import {
  ApiError,
  type PaymentMethodRow,
  type SettlementRow,
  type SumupDirectionPolicy,
  type UserRow,
  api,
} from "../api/client";
import { SumupStuckBadge } from "../components/SumupStuckBadge";
import { SumupReaderSettle } from "../components/SumupReaderSettle";
import { VolunteerDaysSection } from "../components/VolunteerDaysSection";

const { Title, Text } = Typography;

type AmountMode = "full" | "custom";

/** How the operator collects the money in the create-settlement modal:
 * ``manual`` picks an existing payment method (cash, etc.) and books the
 * settlement directly; ``sumup_reader`` arms the operator surface's SumUp
 * Solo reader and the poller produces the settlement once the card taps. */
type SettleMode = "manual" | "sumup_reader";

/** Settlements-page top-level view mode.
 *  - ``per_settlement`` (default, historical): one row per settlement.
 *  - ``per_user``: rolled up to one row per strichliste user, expandable
 *    to the individual settlements. Each user row also links to their
 *    Rechnung / Kontoübersicht (the receipt renderer switches modes
 *    depending on the settlement count). */
type ViewMode = "per_settlement" | "per_user";

const VIEW_MODE_PREF_KEY = "settlements.view_mode";

type ExportFormat = "csv" | "pdf_zip";
type ExportScope = "open_invoice" | "card" | "cash" | "other";

/** Payment-scope checkboxes in the export modal. A settlement/user is
 *  included if it matches ANY ticked scope; all four ≈ everything. */
const EXPORT_SCOPE_OPTIONS: { value: ExportScope; label: string }[] = [
  { value: "open_invoice", label: "Überweisung – noch offen" },
  { value: "card", label: "Karte (SumUp)" },
  { value: "cash", label: "Bar" },
  { value: "other", label: "Alle übrigen" },
];
const ALL_EXPORT_SCOPES: ExportScope[] = EXPORT_SCOPE_OPTIONS.map(
  (o) => o.value,
);

interface PerUserGroup {
  strichliste_user_id: number;
  user_nickname: string | null;
  real_name: string | null;
  settlements: SettlementRow[];
  count: number;
  total_cents: number;
  due_count: number;
  due_cents: number;
  latest_settled_at: string;
}

const STATUS_COLORS: Record<SettlementRow["status"], string> = {
  pending: "blue",
  completed: "green",
  failed: "red",
  pending_blocked: "orange",
  voided: "default",
};

const formatCents = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );
const formatTime = (iso: string) => new Date(iso).toLocaleString();

/** Stable picker key for a user. A record-less strichliste user (a walk-up
 * guest never "touched" in admin) has ``id === null``; we key it on the negated
 * strichliste id so it's still selectable, and create the admin row on the fly
 * at settle time (mirrors the cash-drawer widget). Real admin ids are positive,
 * so the two spaces never collide. */
const userKey = (u: UserRow): number => u.id ?? -u.strichliste_user_id;

export const SettlementsPage = () => {
  const [rows, setRows] = useState<SettlementRow[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("per_settlement");

  // Load persisted view mode once at mount; save on every switch. Falls
  // back to the default when the pref isn't set yet. Shared ui_preference
  // row per convention (Phase 1 — see project_dashboard_ui_preference_phase1).
  useEffect(() => {
    let cancelled = false;
    api
      .getUiPref<ViewMode>(VIEW_MODE_PREF_KEY)
      .then((r) => {
        if (
          !cancelled &&
          (r.value === "per_settlement" || r.value === "per_user")
        ) {
          setViewMode(r.value);
        }
      })
      .catch(() => {
        /* pref unset / API error — keep the default */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const onSwitchViewMode = (next: ViewMode) => {
    setViewMode(next);
    void api.putUiPref(VIEW_MODE_PREF_KEY, next).catch(() => {
      /* pref save is best-effort */
    });
  };

  // Export modal: CSV (per-settlement with per-user aggregate columns) or
  // PDFs zipped (per-user Rechnung / Kontoübersicht). Payment-scope
  // checkboxes choose which settlements/users are included, for both
  // formats. Triggering navigates to the URL so the browser handles the
  // download natively; no cancel token needed.
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>("pdf_zip");
  const [exportScopes, setExportScopes] = useState<ExportScope[]>([
    ...ALL_EXPORT_SCOPES,
  ]);
  const [exportGroupByUser, setExportGroupByUser] = useState(false);
  const triggerExport = () => {
    // All four ticked ≈ everything; send the explicit list either way so
    // the backend applies exactly what's checked.
    const scopes = exportScopes;
    const url =
      exportFormat === "csv"
        ? api.settlementCsvUrl({ scopes, group_by_user: exportGroupByUser })
        : api.settlementZipUrl(scopes);
    // Open in a new tab so the current settlements page + any open
    // modals stay put; the browser's built-in save-as handles the file.
    window.open(url, "_blank", "noopener,noreferrer");
    setExportOpen(false);
  };

  // ?focus=<id> URL param — links from other workspaces (e.g. the cash
  // drawer activity feed → /settlements?focus=42) deep-link to a
  // specific settlement row. After the table loads we scroll the row
  // into view and apply a highlight via onRow. Mirrors the equivalent
  // pattern on ExpensesPage.
  const [searchParams] = useSearchParams();
  const focusId = useMemo(() => {
    const raw = searchParams.get("focus");
    const n = raw !== null ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  useEffect(() => {
    if (focusId === null || rows.length === 0) return;
    const handle = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `tr[data-row-key="${focusId}"]`,
      );
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    return () => window.cancelAnimationFrame(handle);
  }, [focusId, rows]);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [amountMode, setAmountMode] = useState<AmountMode>("full");
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(
    undefined,
  );
  const [partialAmountCents, setPartialAmountCents] = useState<number | null>(
    null,
  );
  // Manual payment method vs SumUp reader. The reader path skips the
  // payment_method picker (the backend stamps the immutable ``card``
  // method) and arms the operator surface's Solo instead.
  const [settleMode, setSettleMode] = useState<SettleMode>("manual");
  // Whether the operator SumUp-reader option is usable (Office probe). Drives a
  // disabled state + note on the "Card — SumUp reader" choice.
  const [sumupOpStatus, setSumupOpStatus] = useState<
    "loading" | "ok" | "no_reader" | "disabled"
  >("loading");
  // Operator-surface top-up bounds from the availability probe (drive the reader
  // amount input, which is set BEFORE priming). topup_max is already 0 unless the
  // direction policy is "both"; min_charge is the ≥€1.00 floor.
  const [sumupOpDirection, setSumupOpDirection] =
    useState<SumupDirectionPolicy>("repayment_only");
  const [sumupOpTopupMax, setSumupOpTopupMax] = useState(0);
  const [sumupOpMinCharge, setSumupOpMinCharge] = useState(100);
  // The reader card-settlement flow runs inside <SumupReaderSettle> once the
  // operator commits ("Charge on reader"). We only track WHETHER it's committed
  // + the resolved amount; the component owns the arm / poll / cancel-on-unmount.
  const [readerCommitted, setReaderCommitted] = useState(false);
  const [readerAmount, setReaderAmount] = useState<number | null>(null);
  const [form] = Form.useForm<{
    user_id: number;
    payment_method_id: number;
    comment?: string;
    disable_after?: boolean;
  }>();

  /** Lookup the live balance (cents, signed) of the user picked in the
   *  modal. Returns ``null`` when no user is picked yet or when the row
   *  somehow lacks a balance (shouldn't happen, but the listUsers result
   *  is typed as nullable so guard for it). */
  const selectedUser = useMemo<UserRow | undefined>(
    () => users.find((u) => userKey(u) === selectedUserId),
    [users, selectedUserId],
  );

  /** Return the admin ``user_identity.id`` for a user, creating a guest row on
   * the fly for a record-less strichliste user so it can be settled. */
  const ensureAdminUserId = async (u: UserRow): Promise<number> => {
    if (u.id != null) return u.id;
    const created = await api.createUser({
      strichliste_user_id: u.strichliste_user_id,
      user_type: "guest",
      name: u.name,
    });
    if (created.id == null) {
      throw new Error("Could not create the guest's admin record.");
    }
    return created.id;
  };
  const selectedAbsBalance =
    selectedUser?.balance_cents != null
      ? Math.abs(selectedUser.balance_cents)
      : null;

  // ---- Amount bounds, FORKED by collection mode --------------------------
  // Manual settles the full balance in EITHER direction (debt repayment AND the
  // operator credit-payout / refund, accounting.md §8) → cap = abs(balance).
  // The SumUp reader always COLLECTS (a live charge), so it bounds on the DEBT
  // and an optional top-up past it (into credit), never on a credit balance.
  const isReaderMode = settleMode === "sumup_reader";
  const selectedAbsDebt =
    selectedUser?.balance_cents != null
      ? Math.max(0, -selectedUser.balance_cents)
      : null;
  // topup_max is already 0 unless the operator surface's direction_policy=both.
  const readerTopupMax = sumupOpDirection === "both" ? sumupOpTopupMax : 0;
  const readerMaxChargeable =
    selectedAbsDebt != null ? selectedAbsDebt + readerTopupMax : null;
  // Active-mode knobs. "full" target is the debt (reader) or the balance (manual)
  // — NEVER the top-up max (the operator types any top-up). min floors at €1.00
  // for the reader (SumUp), 1ct for manual (unchanged).
  const amountFullCents = isReaderMode ? selectedAbsDebt : selectedAbsBalance;
  const amountMaxCents = isReaderMode ? readerMaxChargeable : selectedAbsBalance;
  const amountMinCents = isReaderMode ? sumupOpMinCharge : 1;
  // "Pay debt"/"Full balance" target unusable → no debt to collect (reader) — the
  // operator must enter a custom top-up instead. Manual never disables "full".
  const fullModeDisabled =
    isReaderMode && (selectedAbsDebt == null || selectedAbsDebt < amountMinCents);
  // Custom amount unusable → even the max can't clear the minimum. For the reader
  // that's a credit/zero user with no top-up allowance (repayment_only); manual
  // keeps its legacy "€0 balance → nothing to settle" disable.
  const customModeDisabled = isReaderMode
    ? readerMaxChargeable == null || readerMaxChargeable < amountMinCents
    : selectedAbsBalance == null || selectedAbsBalance === 0;
  // The reader can't collect anything from this user (no debt + no top-up room).
  const readerNothingToCharge = isReaderMode && customModeDisabled;

  // "Reader accepts: …" note. SumUp exposes no per-endpoint methods API (the
  // /payment-methods endpoint is online-checkout only), so the reader's real
  // card-present set is stated explicitly — matching the kiosk's READER_METHODS
  // (settle.js). It accepts girocard/EC + contactless (never in the online list)
  // and all major cards; the online-only PayPal wallet is excluded.
  const readerAcceptsLabel = useMemo(() => {
    const labels: Record<string, string> = {
      girocard: "girocard / EC",
      visa: "Visa",
      mastercard: "Mastercard",
      contactless: "Contactless",
      apple_pay: "Apple Pay",
      google_pay: "Google Pay",
    };
    return ["girocard", "visa", "mastercard", "contactless", "apple_pay", "google_pay"]
      .map((m) => labels[m])
      .join(" · ");
  }, []);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, pms, regular, guest] = await Promise.all([
        api.listSettlements({ status_filter: filterStatus, limit: 200 }),
        api.listPaymentMethods(false),
        api.listUsers({ user_type: "regular", page_size: 200 }),
        api.listUsers({ user_type: "guest", page_size: 200 }),
      ]);
      setRows(s);
      setPaymentMethods(pms);
      setUsers([...regular.items, ...guest.items]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  // Re-fetch when the create modal opens, so the amount cap + "Full balance"
  // label reflect the user's LIVE balance at settlement time (the list snapshot
  // can be minutes stale on a long-lived admin tab). The backend re-validates
  // the amount against the live balance regardless, so this is UX accuracy.
  useEffect(() => {
    if (createOpen) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOpen]);

  // Deep-link from the user-detail "Settle balance" button:
  // /settlements?user_id=<strichliste_user_id> auto-opens a New settlement
  // pre-filled for that user (once the user list has loaded so we can map the
  // strichliste id → user_identity.id the form keys on).
  const settleUserParam = searchParams.get("user_id");
  const [autoOpened, setAutoOpened] = useState(false);
  useEffect(() => {
    if (autoOpened || !settleUserParam || users.length === 0) return;
    setAutoOpened(true);
    const u = users.find(
      (x) => x.strichliste_user_id === Number(settleUserParam),
    );
    if (u != null) {
      setSelectedUserId(userKey(u));
      form.setFieldsValue({ user_id: userKey(u) });
    }
    setPartialAmountCents(null);
    setAmountMode("full");
    setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settleUserParam, users, autoOpened]);

  // Probe once whether the operator SumUp-reader settlement is usable, so the
  // option can be disabled with a clear reason. Any error (incl. a non-Office
  // role's 403) collapses to "disabled".
  useEffect(() => {
    let cancelled = false;
    void api
      .sumupOperatorAvailability()
      .then((r) => {
        if (!cancelled) {
          setSumupOpStatus(r.status);
          setSumupOpDirection(r.direction_policy);
          setSumupOpTopupMax(r.topup_max_cents);
          setSumupOpMinCharge(r.min_charge_cents);
        }
      })
      .catch(() => {
        if (!cancelled) setSumupOpStatus("disabled");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the reader's "Pay debt" target is unusable (a credit/zero user — only a
  // top-up makes sense), fall back to the custom amount so the operator isn't
  // stuck on a disabled radio. Manual never disables "full", so this is reader-only.
  useEffect(() => {
    if (fullModeDisabled && amountMode === "full") setAmountMode("custom");
  }, [fullModeDisabled, amountMode]);

  const closeCreate = () => {
    setCreateOpen(false);
    form.resetFields();
    setAmountMode("full");
    setSelectedUserId(undefined);
    setPartialAmountCents(null);
    setSettleMode("manual");
    // Unmounts <SumupReaderSettle>, which best-effort-cancels a non-terminal
    // intent on its way out.
    setReaderCommitted(false);
    setReaderAmount(null);
  };

  /** Resolve the partial-amount override (cents) from the modal, or null
   *  for a full settlement. Returns ``undefined`` when the custom value is
   *  invalid (and sets the error banner) so callers can bail. */
  const resolveAmountCents = (): number | null | undefined => {
    if (amountMode !== "custom") return null;
    // Validate the custom-amount path on the client so a bad value doesn't
    // even hit the server. The backend re-checks (the only authoritative
    // source for the live balance) but failing fast is a nicer UX. Bounds are
    // mode-forked: manual caps at abs(balance); the reader charges in
    // [min_charge, abs(debt) + topup_max].
    if (
      partialAmountCents == null ||
      !Number.isFinite(partialAmountCents) ||
      partialAmountCents <= 0
    ) {
      setError("Enter an amount greater than 0.");
      return undefined;
    }
    if (partialAmountCents < amountMinCents) {
      setError(`Amount must be at least ${formatCents(amountMinCents)}.`);
      return undefined;
    }
    if (amountMaxCents != null && partialAmountCents > amountMaxCents) {
      setError(`Amount cannot exceed ${formatCents(amountMaxCents)}.`);
      return undefined;
    }
    return partialAmountCents;
  };

  const onCreate = async () => {
    if (settleMode === "sumup_reader") {
      // Commit → mount <SumupReaderSettle>, which arms + polls. Validate the
      // user + (custom) amount here; the component does the rest.
      if (selectedUserId == null) {
        setError("Select a user first.");
        return;
      }
      const amount_cents = resolveAmountCents();
      if (amount_cents === undefined) return; // invalid custom amount
      const user = users.find((u) => userKey(u) === selectedUserId);
      if (user == null) {
        setError("Select a user first.");
        return;
      }
      try {
        // Create the admin record on the fly for a record-less guest so the
        // reader settlement links to it.
        await ensureAdminUserId(user);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return;
      }
      setError(null);
      setReaderAmount(amount_cents); // null = full debt
      setReaderCommitted(true);
      return;
    }
    try {
      const values = await form.validateFields();
      const amount_cents = resolveAmountCents();
      if (amount_cents === undefined) return; // invalid custom amount
      // The picker keys on ``userKey`` (admin id, or a negated strichliste id
      // for record-less guests); resolve it to a real admin id, creating the
      // guest row on the fly if needed.
      const picked = users.find((u) => userKey(u) === values.user_id);
      if (picked == null) {
        setError("Select a user.");
        return;
      }
      const adminUserId = await ensureAdminUserId(picked);
      const created = await api.createSettlement({
        ...values,
        user_id: adminUserId,
        ...(amount_cents != null ? { amount_cents } : {}),
      });
      closeCreate();
      await refresh();
      // Bank transfer (pay-by-invoice) is unpaid → force the invoice email so the
      // customer receives the open amount to transfer.
      const pm = paymentMethods.find((m) => m.id === values.payment_method_id);
      if (pm?.code === "invoice") {
        void emailInvoice(created.id);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (
        err &&
        typeof err === "object" &&
        "errorFields" in err
      ) {
        // form validation; ignore
      } else {
        setError(String(err));
      }
    }
  };

  const onVoid = async (row: SettlementRow) => {
    try {
      await api.voidSettlement(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onReconcile = async () => {
    try {
      const summary = await api.reconcileSettlements();
      setError(
        `Reconciled: matched=${summary.matched}, expired=${summary.expired}, still_pending=${summary.still_pending}`,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  // Open the operator's mail client with the UNIFIED per-user invoice (all of the
  // user's settlements + one total VAT split) pre-filled. mailto: can't attach —
  // the same document is the receipt.html.
  const emailInvoice = async (id: number) => {
    try {
      const inv = await api.getSettlementInvoice(id);
      if (!inv.user_email) {
        notification.info({ message: "Keine E-Mail-Adresse für diesen Nutzer" });
        return;
      }
      const eur = (c: number) => `${(c / 100).toFixed(2).replace(".", ",")} €`;
      const L: string[] = [
        `Rechnung / Kontoübersicht — ${inv.user_name}`,
        "",
        "Übersicht aller Abrechnungen:",
      ];
      for (const stm of inv.settlements) {
        L.push(
          `  ${stm.receipt_no}  ${(stm.settled_at ?? "").slice(0, 16).replace("T", " ")}  ${stm.payment_method}${stm.due ? " (fällig)" : ""}  ${eur(stm.amount_cents)}`,
        );
      }
      L.push("", "MwSt-Aufstellung (gesamt):");
      for (const g of inv.vat_groups) {
        L.push(
          `  ${g.percent}%   netto ${eur(g.net_cents)}   MwSt ${eur(g.vat_cents)}   brutto ${eur(g.gross_cents)}`,
        );
      }
      L.push(
        `  Gesamt: netto ${eur(inv.consumption_net_cents)}   MwSt ${eur(inv.consumption_vat_cents)}   brutto ${eur(inv.consumption_gross_cents)}`,
        "",
        `Konsum brutto: ${eur(inv.consumption_gross_cents)}`,
      );
      if (inv.credits_cents > 0)
        L.push(`- Gutschriften: ${eur(inv.credits_cents)}`);
      if (inv.amount_paid_cents > 0) L.push(`Beglichen: ${eur(inv.amount_paid_cents)}`);
      if (inv.amount_due_cents > 0)
        L.push(`Amount due (Überweisung): ${eur(inv.amount_due_cents)}`);
      if (inv.open_balance_cents < 0)
        L.push(`Offen (nicht abgerechnet): ${eur(-inv.open_balance_cents)}`);
      else if (inv.open_balance_cents > 0)
        L.push(`Guthaben: ${eur(inv.open_balance_cents)}`);
      const href = `mailto:${encodeURIComponent(inv.user_email)}?subject=${encodeURIComponent(
        `Rechnung ${inv.user_name}`,
      )}&body=${encodeURIComponent(L.join("\n"))}`;
      // Open via a new-window anchor so the SPA tab is never navigated away.
      const a = document.createElement("a");
      a.href = href;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      notification.error({
        message: "Rechnung konnte nicht geladen werden",
        description: err instanceof ApiError ? err.message : String(err),
      });
    }
  };

  // --- Per-user grouping ---
  // Roll the flat settlements list into one row per strichliste user.
  // Voided settlements are dropped from every aggregate (they don't
  // represent real cash movement); they remain visible in the flat
  // per-settlement view for audit. Bank-transfer settlements
  // (payment_method.code === "invoice") count toward the "Noch offen"
  // aggregate — they're outstanding demands the user hasn't paid yet.
  const invoicePmIds = useMemo(
    () =>
      new Set(
        paymentMethods
          .filter((p) => p.code === "invoice")
          .map((p) => p.id),
      ),
    [paymentMethods],
  );
  const usersBySid = useMemo(() => {
    const m = new Map<number, UserRow>();
    for (const u of users) m.set(u.strichliste_user_id, u);
    return m;
  }, [users]);
  const perUserRows: PerUserGroup[] = useMemo(() => {
    const byUser = new Map<number, PerUserGroup>();
    for (const s of rows) {
      if (s.status === "voided") continue;
      const sid = s.strichliste_user_id;
      let g = byUser.get(sid);
      if (!g) {
        const u = usersBySid.get(sid);
        g = {
          strichliste_user_id: sid,
          user_nickname: s.user_nickname,
          real_name: u?.real_name ?? null,
          settlements: [],
          count: 0,
          total_cents: 0,
          due_count: 0,
          due_cents: 0,
          latest_settled_at: s.settled_at,
        };
        byUser.set(sid, g);
      }
      g.settlements.push(s);
      g.count += 1;
      g.total_cents += s.amount_cents;
      if (invoicePmIds.has(s.payment_method_id)) {
        g.due_count += 1;
        g.due_cents += s.amount_cents;
      }
      if (s.settled_at > g.latest_settled_at) g.latest_settled_at = s.settled_at;
    }
    // Sort settlements within each user by settled_at desc (newest first)
    // so the expandable table reads naturally when the operator opens a user.
    for (const g of byUser.values()) {
      g.settlements.sort((a, b) => b.settled_at.localeCompare(a.settled_at));
    }
    // Sort user rows by latest settled_at desc.
    return Array.from(byUser.values()).sort((a, b) =>
      b.latest_settled_at.localeCompare(a.latest_settled_at),
    );
  }, [rows, usersBySid, invoicePmIds]);

  const perUserColumns: ColumnsType<PerUserGroup> = [
    {
      title: "User",
      dataIndex: "strichliste_user_id",
      render: (_v: number, g) => {
        const nick = g.user_nickname ?? `#${g.strichliste_user_id}`;
        if (g.real_name && g.real_name !== nick) {
          return (
            <Space size={4}>
              <b>{g.real_name}</b>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({nick})
              </Text>
            </Space>
          );
        }
        return <b>{nick}</b>;
      },
    },
    {
      title: "Abrechnungen",
      dataIndex: "count",
      align: "right" as const,
      sorter: (a, b) => a.count - b.count,
      render: (v: number) => v,
    },
    {
      title: "Gesamt",
      dataIndex: "total_cents",
      align: "right" as const,
      sorter: (a, b) => a.total_cents - b.total_cents,
      render: (v: number) => formatCents(v),
    },
    {
      title: "Noch offen",
      dataIndex: "due_cents",
      align: "right" as const,
      sorter: (a, b) => a.due_cents - b.due_cents,
      render: (v: number, g) =>
        g.due_count > 0 ? (
          <Text style={{ color: "var(--ant-color-error, #cf1322)", fontWeight: 600 }}>
            {formatCents(v)}{" "}
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({g.due_count})
            </Text>
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Letzte Abrechnung",
      dataIndex: "latest_settled_at",
      sorter: (a, b) => a.latest_settled_at.localeCompare(b.latest_settled_at),
      defaultSortOrder: "descend" as const,
      render: (v: string) => formatTime(v),
    },
    {
      title: "",
      key: "actions",
      render: (_: unknown, g) => {
        // The receipt route builds a per-user invoice, so any of the
        // user's settlement ids works as the entry point. The renderer
        // auto-switches between Rechnung (1 settlement) and
        // Kontoübersicht (2+). Newest gives the most useful default.
        const anySid = g.settlements[0]?.id;
        if (anySid == null) return null;
        return (
          <Space size={4}>
            <a
              href={api.settlementReceiptUrl(anySid)}
              target="_blank"
              rel="noreferrer"
            >
              📄 {g.count === 1 ? "Rechnung" : "Kontoübersicht"}
            </a>
            <Button
              type="link"
              size="small"
              title="Rechnung per E-Mail an den Nutzer"
              onClick={() => void emailInvoice(anySid)}
            >
              ✉
            </Button>
          </Space>
        );
      },
    },
  ];

  const columns: ColumnsType<SettlementRow> = [
    {
      title: "Receipt #",
      dataIndex: "receipt_no",
      render: (v: string, row) => (
        <Space size={6}>
          <a
            href={api.settlementReceiptUrl(row.id)}
            target="_blank"
            rel="noreferrer"
          >
            {v}
          </a>
          <Button
            type="link"
            size="small"
            title="Rechnung per E-Mail an den Nutzer"
            onClick={() => void emailInvoice(row.id)}
          >
            ✉
          </Button>
        </Space>
      ),
    },
    {
      title: "Settled at",
      dataIndex: "settled_at",
      render: (v: string) => formatTime(v),
    },
    { title: "User", dataIndex: "user_nickname" },
    {
      title: "Amount",
      dataIndex: "amount_cents",
      align: "right" as const,
      render: (v: number) => formatCents(v),
    },
    {
      title: "Direction",
      dataIndex: "direction",
      render: (v: SettlementRow["direction"]) => (
        <Tag>{v === "user_to_bank" ? "→ bank" : v === "bank_to_user" ? "← bank" : "—"}</Tag>
      ),
    },
    { title: "Payment", dataIndex: "payment_method_name" },
    {
      title: "Status",
      dataIndex: "status",
      render: (v: SettlementRow["status"]) => (
        <Tag color={STATUS_COLORS[v]}>{v}</Tag>
      ),
    },
    { title: "Comment", dataIndex: "comment", ellipsis: true },
    {
      title: "Actions",
      render: (_: unknown, row) =>
        row.status === "pending" ||
        row.status === "failed" ||
        row.status === "pending_blocked" ? (
          <Popconfirm
            title="Void this settlement?"
            onConfirm={() => onVoid(row)}
            okText="Void"
          >
            <Button danger size="small">
              Void
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  return (
    <Card>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}>
        <Space align="center">
          <Title level={2} style={{ margin: 0 }}>
            Settlements
          </Title>
          {/* Paid-but-unsettled SumUp card payments being finalized by the
              poller — links to the filtered transactions view. Hidden at 0. */}
          <SumupStuckBadge />
        </Space>
        <Space>
          <Segmented<ViewMode>
            value={viewMode}
            onChange={(v) => onSwitchViewMode(v as ViewMode)}
            options={[
              { label: "Pro Abrechnung", value: "per_settlement" },
              { label: "Pro Nutzer", value: "per_user" },
            ]}
          />
          {viewMode === "per_settlement" && (
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 200 }}
              value={filterStatus}
              onChange={(v) => setFilterStatus(v)}
              options={[
                { value: "pending", label: "Pending" },
                { value: "completed", label: "Completed" },
                { value: "failed", label: "Failed" },
                { value: "pending_blocked", label: "Blocked" },
                { value: "voided", label: "Voided" },
              ]}
            />
          )}
          <Button onClick={onReconcile}>Reconcile now</Button>
          <Button onClick={() => setExportOpen(true)}>Export…</Button>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            + New settlement
          </Button>
        </Space>
      </Space>
      {error && (
        <Alert
          type="info"
          message={error}
          style={{ marginBottom: 16 }}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}
      {viewMode === "per_settlement" ? (
        <Table<SettlementRow>
          columns={columns}
          dataSource={rows}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 25 }}
          onRow={(row) => ({
            style:
              focusId !== null && row.id === focusId
                ? {
                    background: "var(--ant-color-warning-bg, #fff7e6)",
                    outline: "2px solid var(--ant-color-warning-border, #ffd591)",
                  }
                : undefined,
          })}
        />
      ) : (
        <Table<PerUserGroup>
          columns={perUserColumns}
          dataSource={perUserRows}
          rowKey="strichliste_user_id"
          loading={loading}
          pagination={{ pageSize: 25 }}
          // Auto-expand the row that contains the focused settlement so
          // deep-links from other workspaces (?focus=<id>) still land
          // the user on the right row's expanded view.
          defaultExpandedRowKeys={
            focusId !== null
              ? perUserRows
                  .filter((g) => g.settlements.some((s) => s.id === focusId))
                  .map((g) => g.strichliste_user_id)
              : undefined
          }
          expandable={{
            // The nested table reuses the flat per-settlement columns so
            // the operator sees the same shape they know — just scoped
            // to one user. Actions (void, receipt link, ✉) work as-is.
            expandedRowRender: (g) => (
              <Table<SettlementRow>
                columns={columns}
                dataSource={g.settlements}
                rowKey="id"
                pagination={false}
                size="small"
                onRow={(row) => ({
                  style:
                    focusId !== null && row.id === focusId
                      ? {
                          background: "var(--ant-color-warning-bg, #fff7e6)",
                          outline: "2px solid var(--ant-color-warning-border, #ffd591)",
                        }
                      : undefined,
                })}
              />
            ),
          }}
        />
      )}
      <Modal
        title="Export"
        open={exportOpen}
        onCancel={() => setExportOpen(false)}
        onOk={triggerExport}
        okText="Export"
        okButtonProps={{ disabled: exportScopes.length === 0 }}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="Format">
            <Radio.Group
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as ExportFormat)}
            >
              <Radio value="pdf_zip">PDFs (ZIP)</Radio>
              <Radio value="csv">CSV</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Umfang (Zahlarten)"
            extra="Mehrfachauswahl — enthalten ist jede Abrechnung der angehakten Zahlarten. Überweisung – noch offen schließt stornierte automatisch aus."
          >
            <Checkbox.Group
              value={exportScopes}
              onChange={(vals) => setExportScopes(vals as ExportScope[])}
              options={EXPORT_SCOPE_OPTIONS}
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            />
          </Form.Item>
          {exportFormat === "csv" && (
            <Form.Item style={{ marginBottom: 8 }}>
              <Checkbox
                checked={exportGroupByUser}
                onChange={(e) => setExportGroupByUser(e.target.checked)}
              >
                Eine Zeile pro Nutzer
              </Checkbox>
            </Form.Item>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {exportFormat === "pdf_zip" ? (
              <>
                Für jeden Nutzer wird eine PDF erzeugt (Rechnung bei einer
                einzigen Abrechnung; Kontoübersicht bei mehreren). Dateinamen:{" "}
                <code>{"{Beleg}"}.pdf</code> bzw.{" "}
                <code>{"{UserID}_{Name}"}.pdf</code>.
              </>
            ) : exportGroupByUser ? (
              <>
                Eine Zeile pro <b>Nutzer</b> — die Belegnummern der gültigen
                Abrechnungen mit <code>;</code> verkettet, Beträge summiert;
                stornierte separat in <code>corrected_receipt_nos</code>.
              </>
            ) : (
              <>
                Eine Zeile pro <b>Abrechnung</b>, mit zusätzlichen Spalten pro
                Nutzer (Amount due, Getränke/Lebensmittel/Kleidung) und einer{" "}
                <code>corrected</code>-Spalte für stornierte Abrechnungen.
              </>
            )}
          </Text>
        </Form>
      </Modal>
      <Modal
        title="New settlement"
        open={createOpen}
        onCancel={closeCreate}
        // Once the card has settled the only action left is to close; before
        // that the primary either creates the manual settlement or arms the
        // reader. OK is disabled while a reader prime/poll is in flight.
        okText={settleMode === "sumup_reader" ? "Charge on reader" : "Create"}
        okButtonProps={{
          // Hidden once committed — the customer taps/scans inside the panel.
          style: readerCommitted ? { display: "none" } : undefined,
          disabled: isReaderMode && readerNothingToCharge,
        }}
        cancelText={readerCommitted ? "Close" : "Cancel"}
        onOk={onCreate}
        // Bigger tap targets for the touch-first operator surface.
        width={520}
      >
        <Form form={form} layout="vertical" size="large">
          {/* Collection method: manual payment method vs SumUp Solo reader.
              The reader path stamps the immutable ``card`` method server-side. */}
          <Form.Item label="How are you collecting?">
            <Radio.Group
              value={settleMode}
              disabled={readerCommitted}
              onChange={(e) => {
                setSettleMode(e.target.value as SettleMode);
                setError(null);
              }}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="manual">Manual / cash</Radio.Button>
              <Radio.Button
                value="sumup_reader"
                disabled={sumupOpStatus !== "ok"}
              >
                Card — SumUp reader
              </Radio.Button>
            </Radio.Group>
            {sumupOpStatus !== "ok" && sumupOpStatus !== "loading" && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">
                  {sumupOpStatus === "no_reader"
                    ? "No SumUp reader configured for the operator surface — set one in Settings → SumUp."
                    : "SumUp is disabled — enable it in Settings → SumUp."}
                </Text>
              </div>
            )}
            {sumupOpStatus === "ok" && readerAcceptsLabel && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">Reader accepts: {readerAcceptsLabel}</Text>
              </div>
            )}
          </Form.Item>
          <Form.Item
            label="User"
            name="user_id"
            rules={[{ required: true, message: "Select a user" }]}
          >
            <Select
              showSearch
              placeholder="Type to filter"
              optionFilterProp="label"
              onChange={(v: number) => {
                setSelectedUserId(v);
                // Reset partial amount so we don't carry a stale value from a
                // previous user with a larger balance.
                setPartialAmountCents(null);
                setAmountMode("full");
              }}
              disabled={readerCommitted}
              options={users
                // Bank is never settleable. Record-less strichliste users
                // (walk-up guests, ``id=null``) ARE included — keyed on
                // ``userKey`` and given an admin row on the fly at settle time.
                .filter((u) => u.user_type !== "bank")
                .map((u) => ({
                  value: userKey(u),
                  label: `${u.name}${u.real_name ? ` (${u.real_name})` : ""}${
                    u.id == null ? " (guest — new)" : ""
                  } — ${
                    u.balance_cents != null ? formatCents(u.balance_cents) : "?"
                  }`,
                }))}
            />
          </Form.Item>
          {/* Volunteer compensation: if the selected user has unpaid V-days,
              offer to pay them out (reducing the debt) BEFORE settling. */}
          {selectedUser?.strichliste_user_id != null && !readerCommitted && (
            <VolunteerDaysSection
              compact
              strichlisteUserId={selectedUser.strichliste_user_id}
              onChanged={() => void refresh()}
            />
          )}
          {settleMode === "manual" && (
            <Form.Item
              label="Payment method"
              name="payment_method_id"
              rules={[{ required: true, message: "Select a payment method" }]}
            >
              <Select
                options={paymentMethods.map((pm) => ({
                  value: pm.id,
                  label: pm.name,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item label="Amount">
            <Radio.Group
              value={amountMode}
              disabled={readerCommitted}
              onChange={(e) => {
                const next = e.target.value as AmountMode;
                setAmountMode(next);
                if (next === "full") {
                  setPartialAmountCents(null);
                }
              }}
            >
              <Radio.Button value="full" disabled={fullModeDisabled}>
                {isReaderMode ? "Pay debt" : "Full balance"}
                {amountFullCents != null
                  ? ` (${formatCents(amountFullCents)})`
                  : ""}
              </Radio.Button>
              <Radio.Button value="custom" disabled={customModeDisabled}>
                {isReaderMode ? "Custom / top-up" : "Custom amount"}
              </Radio.Button>
            </Radio.Group>
            {readerNothingToCharge && (
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">
                  Nothing to collect from this user — no debt, and top-up isn’t
                  enabled for the operator surface.
                </Text>
              </div>
            )}
          </Form.Item>
          {amountMode === "custom" && !readerNothingToCharge && (
            <Form.Item
              label={isReaderMode ? "Charge amount" : "Partial amount"}
              extra={(() => {
                if (partialAmountCents == null) {
                  if (isReaderMode) {
                    return readerTopupMax > 0
                      ? "Enter how much to charge — above the debt tops up into credit."
                      : "Enter how much of the debt to collect now.";
                  }
                  return "Enter how much of the balance to settle now.";
                }
                if (isReaderMode && selectedAbsDebt != null) {
                  if (partialAmountCents < selectedAbsDebt) {
                    return `Will leave ${formatCents(
                      selectedAbsDebt - partialAmountCents,
                    )} unpaid`;
                  }
                  if (partialAmountCents > selectedAbsDebt) {
                    return `Adds ${formatCents(
                      partialAmountCents - selectedAbsDebt,
                    )} to the balance (credit)`;
                  }
                  return "Settles the debt exactly";
                }
                return selectedAbsBalance != null
                  ? `Will leave ${formatCents(
                      Math.max(0, selectedAbsBalance - partialAmountCents),
                    )} unpaid`
                  : "Enter how much of the balance to settle now.";
              })()}
            >
              <InputNumber<number>
                style={{ width: "100%" }}
                min={amountMinCents}
                max={amountMaxCents ?? undefined}
                step={1}
                precision={0}
                addonAfter="ct"
                placeholder="e.g. 3000"
                value={partialAmountCents ?? undefined}
                onChange={(v) =>
                  setPartialAmountCents(v == null ? null : Math.round(v))
                }
              />
            </Form.Item>
          )}
          <Form.Item label="Comment (optional)" name="comment">
            <Input.TextArea rows={2} maxLength={1024} />
          </Form.Item>
          <Form.Item name="disable_after" valuePropName="checked">
            <Checkbox disabled={readerCommitted}>
              Disable user after settlement
              {amountMode === "custom"
                ? " (partial + disable accepts the remainder as bad debt)"
                : ""}
            </Checkbox>
          </Form.Item>

          {settleMode === "sumup_reader" && readerCommitted && selectedUser && (
            <SumupReaderSettle
              strichlisteUserId={selectedUser.strichliste_user_id}
              amountCents={readerAmount}
              onSettled={() => void refresh()}
              onSwitchToManual={() => {
                setReaderCommitted(false);
                setSettleMode("manual");
              }}
            />
          )}
        </Form>
      </Modal>
    </Card>
  );
};

